/* Code v2 — the repos. Org roll-up stat strip · CI status (big numbers) ·
   commit-velocity sparkline · repo filter + open PRs · per-repo health table ·
   deploy history (DS Table). Wired to GitHub + Cloudflare Pages; ciRate/alerts
   render '—' when the source can't answer (null) — never a fake number, and
   every status carries a dot AND a word (color is never the only signal). */
import { useState } from 'react';
import { Sparkline } from '../../ds/mc-kit';
import { Badge, Select, StatusPill, Table } from '../../ds';
import { useContract } from '../../lib/api';
import { Widget } from '../ui';
import { integrationFor } from '../../lib/flags';
import { deriveCode } from '../../lib/selectors';
import type { RepoStat } from '../../lib/contract';

/* ---- Command's statCard idiom (quick-stats) ---- */
const statCard: React.CSSProperties = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '15px 17px', minWidth: 0 };
const statHead: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 };
const statLabel: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)' };
const statBig: React.CSSProperties = { fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 26, letterSpacing: '-0.02em', color: 'var(--ink)' };
const statFoot: React.CSSProperties = { marginTop: 14, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' };

const mono: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--muted)' };

/** Dot + word — the status idiom for the stat-card corner (never color alone). */
function DotWord({ color, word }: { color: string; word: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color }}>
      <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
      {word}
    </span>
  );
}

/** Badge with a leading dot — flags carry a dot AND a word. */
function DotBadge({ tone, children }: { tone: 'warn' | 'danger'; children: React.ReactNode }) {
  return (
    <Badge tone={tone} mono>
      <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor', flex: 'none' }} />
      {children}
    </Badge>
  );
}

/** needsAttention reasons as amber word-badges; '—' when the repo is clear. */
function AttentionCell({ r }: { r: RepoStat }) {
  if (!r.needsAttention.length) return <span style={mono}>—</span>;
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6 }}>
      {r.needsAttention.map((why) => (
        <DotBadge key={why} tone="warn">{why}</DotBadge>
      ))}
    </span>
  );
}

/** Dependabot alert count: coral badge with count + the word 'alerts' when >0;
    honest '—' when the source couldn't answer (null ≠ 0). */
function AlertsCell({ alerts }: { alerts: number | null }) {
  if (alerts == null) return <span style={mono}>—</span>;
  if (alerts === 0) return <span style={mono}>0</span>;
  return <DotBadge tone="danger">{alerts} alerts</DotBadge>;
}

const fmtRate = (rate: number | null) => (rate != null ? `${rate}%` : '—');

function LastRunCell({ lastRun }: { lastRun: RepoStat['lastRun'] }) {
  if (!lastRun) return <span style={mono}>—</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
      <StatusPill status={lastRun.status} />
      <span style={mono}>{lastRun.when}</span>
    </span>
  );
}

/* ---- CI status columns (v1 idiom, kept 1:1) ---- */
const CI_COLS: { key: 'passed' | 'failed' | 'running' | 'queued'; dot: React.CSSProperties }[] = [
  { key: 'passed', dot: { background: 'var(--accent)' } },
  { key: 'failed', dot: { background: 'var(--danger)' } },
  { key: 'running', dot: { background: 'var(--primary)', boxShadow: '0 0 7px var(--primary)', animation: 'ct-pulse 2s ease-in-out infinite' } },
  { key: 'queued', dot: { background: 'var(--muted)' } },
];

/* ---- the selected repo's stat row (under the filter) ---- */
function RepoStatRow({ r }: { r: RepoStat }) {
  const cell = (label: string, value: React.ReactNode) => (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)' }}>{label}</span>
      <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>{value}</span>
    </span>
  );
  return (
    <div role="group" aria-label={`${r.name} repo stats`} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '10px 20px', padding: '11px 13px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
      {cell('open prs', r.openPrs)}
      {cell('awaiting review', r.awaitingReview)}
      {cell('oldest', r.oldestPr)}
      {cell('ci rate', fmtRate(r.ciRate))}
      {cell('issues', r.openIssues)}
      {cell('release', r.release)}
      {cell('alerts', <AlertsCell alerts={r.alerts} />)}
      {r.needsAttention.length > 0 && <AttentionCell r={r} />}
    </div>
  );
}

export function Code() {
  const code = useContract('code', { pollMs: 20_000 });
  const connect = integrationFor('code')?.connect;
  const [repoFilter, setRepoFilter] = useState('all');

  // Selection survives data refreshes; a vanished repo falls back to "all".
  const repos = code.data?.repos ?? [];
  const filter = repoFilter !== 'all' && repos.some((r) => r.name === repoFilter) ? repoFilter : 'all';

  return (
    <div data-screen-label="Code" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--mc-gap)' }}>

      {/* === ORG ROLL-UP — awaiting review · CI pass-rate · alerts · issues === */}
      <Widget query={code} title="// org roll-up" minHeight={92} rows={1} connect={connect} badge="connect me"
        errorLabel="GitHub unavailable" errorSub="Couldn't reach the org." emptyLabel="No repos yet">
        {(c) => {
          const { org } = c;
          const ciWord = org.ciRate == null
            ? { color: 'var(--muted)', word: 'no runs' }
            : org.ciRate >= 90
              ? { color: 'var(--accent-ink)', word: 'healthy' }
              : org.ciRate >= 70
                ? { color: 'var(--warn)', word: 'watch' }
                : { color: 'var(--danger)', word: 'failing' };
          const alertWord = org.alerts == null
            ? { color: 'var(--muted)', word: 'no access' }
            : org.alerts > 0
              ? { color: 'var(--danger)', word: 'attention' }
              : { color: 'var(--accent-ink)', word: 'clear' };
          return (
            <div className="mc-auto mc-auto-md">
              <div style={statCard}>
                <div style={statHead}><span style={statLabel}>Awaiting review</span></div>
                <div style={statBig}>{org.awaitingReview}</div>
                <div style={statFoot}>open PRs waiting on a reviewer</div>
              </div>
              <div style={statCard}>
                <div style={statHead}><span style={statLabel}>CI pass rate</span><DotWord color={ciWord.color} word={ciWord.word} /></div>
                <div style={statBig}>{fmtRate(org.ciRate)}</div>
                <div style={statFoot}>recent runs · org-wide</div>
              </div>
              <div style={statCard}>
                <div style={statHead}><span style={statLabel}>Security alerts</span><DotWord color={alertWord.color} word={alertWord.word} /></div>
                <div style={statBig}>{org.alerts ?? '—'}</div>
                <div style={statFoot}>{org.alerts == null ? 'needs Dependabot read on the PAT' : 'Dependabot · open, org-wide'}</div>
              </div>
              <div style={statCard}>
                <div style={statHead}><span style={statLabel}>Open issues</span></div>
                <div style={statBig}>{org.openIssues}</div>
                <div style={statFoot}>excludes pull requests</div>
              </div>
            </div>
          );
        }}
      </Widget>

      {/* === ROW 2: CI status (left 1fr) + velocity (right 1.1fr) === */}
      <div className="mc-row-2" style={{ '--mc-2': '1fr 1.1fr' } as React.CSSProperties}>
        {/* CI status — four big-number columns */}
        <Widget query={code} title="// ci status" minHeight={92} rows={2} connect={connect} badge="connect me"
          errorLabel="CI unavailable" emptyLabel="No runs">
          {(c) => {
            const { ci } = deriveCode(c);
            return (
              <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap' }}>
                {CI_COLS.map(({ key, dot }) => (
                  <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: 'var(--font-mono)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted)' }}>
                      <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', ...dot }} />
                      {key}
                    </span>
                    <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 25, color: 'var(--ink)' }}>{ci[key]}</span>
                  </div>
                ))}
              </div>
            );
          }}
        </Widget>

        {/* Commit velocity sparkline */}
        <Widget query={code} title="// commit velocity · 14d" minHeight={92} rows={2} connect={connect} badge="connect me"
          aside={<span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>{code.data ? deriveCode(code.data).velocityLabel : ''}</span>}
          errorLabel="History unavailable" emptyLabel="No commits">
          {(c) => {
            const d = deriveCode(c);
            return <Sparkline data={d.velocity} height={92} tone="cyan" />;
          }}
        </Widget>
      </div>

      {/* === OPEN PRs — with the repo filter + the selected repo's stat row === */}
      <Widget query={code} title="// open prs" minHeight={180} rows={4} connect={connect} badge="connect me"
        aside={code.data ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, color: 'var(--warn)', background: 'var(--warn-soft)', border: '1px solid var(--warn-line)' }}>oldest · {deriveCode(code.data).oldest}</span> : undefined}
        errorLabel="GitHub unavailable" errorSub="Couldn't reach the Actions API." emptyLabel="No open PRs" emptySub="The queue is clear.">
        {(c) => {
          const all = deriveCode(c).prs;
          const prs = filter === 'all' ? all : all.filter((p) => p.repo === filter);
          const selected = filter === 'all' ? undefined : c.repos.find((r) => r.name === filter);
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ maxWidth: 300 }}>
                <Select label="Filter by repo" value={filter} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setRepoFilter(e.target.value)}>
                  <option value="all">All repos</option>
                  {c.repos.map((r) => (
                    <option key={r.name} value={r.name}>{r.name}</option>
                  ))}
                </Select>
              </div>
              {selected && <RepoStatRow r={selected} />}
              {prs.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '14px 2px', fontSize: 13, color: 'var(--muted)' }}>
                  <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', flex: 'none' }} />
                  No open PRs{filter === 'all' ? '' : ` in ${filter}`} — the queue is clear.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {prs.map((p) => (
                    <div key={`${p.repo}-${p.id}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--muted)', width: 42, flex: 'none' }}>#{p.id}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, color: 'var(--ink)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>{p.repo} · {p.author} · {p.age}</div>
                      </div>
                      <StatusPill status={p.checks} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        }}
      </Widget>

      {/* === PER-REPO HEALTH (DS Table) === */}
      <Widget query={code} title="// repos" minHeight={170} rows={4} connect={connect} badge="connect me"
        errorLabel="GitHub unavailable" errorSub="Couldn't reach the repos API." emptyLabel="No repos yet" emptySub="The org has no active repositories.">
        {(c) => {
          if (!c.repos.length) {
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '14px 2px', fontSize: 13, color: 'var(--muted)' }}>
                <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--muted)', flex: 'none' }} />
                No repos yet — the org list came back empty.
              </div>
            );
          }
          const columns = [
            { key: 'repo', label: 'repo' },
            { key: 'prs', label: 'open prs', align: 'right' as const },
            { key: 'review', label: 'awaiting review', align: 'right' as const },
            { key: 'ci', label: 'ci rate', align: 'right' as const },
            { key: 'run', label: 'last run' },
            { key: 'issues', label: 'issues', align: 'right' as const },
            { key: 'release', label: 'release' },
            { key: 'alerts', label: 'alerts' },
            { key: 'attention', label: 'attention' },
          ];
          const rows = c.repos.map((r) => ({
            id: r.name,
            repo: <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--accent-ink)' }}>{r.name}</span>,
            prs: r.openPrs,
            review: r.awaitingReview,
            ci: <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: r.ciRate == null ? 'var(--muted)' : 'var(--ink)' }}>{fmtRate(r.ciRate)}</span>,
            run: <LastRunCell lastRun={r.lastRun} />,
            issues: r.openIssues,
            release: <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: r.release === '—' ? 'var(--muted)' : 'var(--ink)' }}>{r.release}</span>,
            alerts: <AlertsCell alerts={r.alerts} />,
            attention: <AttentionCell r={r} />,
          }));
          return (
            <div className="mc-table-wrap" tabIndex={0} role="region" aria-label="Per-repo health table (scrollable)">
              <Table columns={columns} rows={rows} style={{ minWidth: 820 }} />
            </div>
          );
        }}
      </Widget>

      {/* === DEPLOY HISTORY (DS Table) === */}
      <Widget query={code} title="// deploy history" minHeight={170} rows={4} connect={connect} badge="connect me"
        errorLabel="Deploys unavailable" emptyLabel="No deploys yet">
        {(c) => {
          const { deploys } = deriveCode(c);
          const columns = [
            { key: 'commit', label: 'commit' },
            { key: 'repo', label: 'repo' },
            { key: 'env', label: 'env' },
            { key: 'status', label: 'status' },
            { key: 'when', label: 'when', align: 'right' as const },
          ];
          const rows = deploys.map((d, i) => ({
            key: i,
            commit: <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--accent-ink)' }}>{d.sha}</span>,
            repo: d.repo,
            env: <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: d.envColor }}>{d.env}</span>,
            status: <StatusPill status={d.status} />,
            when: d.time,
          }));
          return <div className="mc-table-wrap" tabIndex={0} role="region" aria-label="Deploy history table (scrollable)"><Table columns={columns} rows={rows} /></div>;
        }}
      </Widget>
    </div>
  );
}
